// Copyright (c) 2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';

import { ReportInformationType } from "@shared/constants/data-management";
import { REPORT_PARAMS } from '@shared/constants/services';
import { FilterItems } from '@shared/models/jobrequesttimelines';

@injectable()
export default class ReportsService {
  protected urlPrefix = 'reports';
  static diToken = Symbol('reports-service');

  getReportQuery(reportType: ReportInformationType, orderId: number, mapBoundary?: string, filters?: Array<FilterItems> | undefined) : string {
    const vendorIdParam = 'vendorId: $vendorId';
    const orderIdParam =  orderId >= 0 ? `orderId: ${orderId}` : '';
    const mapBoundaryParam = (mapBoundary !== undefined && mapBoundary !== null && mapBoundary.length >= 7) ? `mapBoundary: "${mapBoundary}"` : '';
    
    // The API uses GraphQL to parse the payload, and it doesn't accept double-quotes
    const filterParam = filters? `filter: "${JSON.stringify(filters).replace(/"/g, '%22')}"` : '';
    
    switch (reportType) {
        case ReportInformationType.collection:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.flightCollection},
                    ${REPORT_PARAMS.deliverables.fieldQC},
                    ${REPORT_PARAMS.deliverables.sendFlightCollection},
                }
            }`;
        case ReportInformationType.process:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.receiveFlightCollection},
                    ${REPORT_PARAMS.deliverables.createProjectFolder},
                    ${REPORT_PARAMS.deliverables.verifyFlightCollection},
                    ${REPORT_PARAMS.deliverables.geotag},
                    ${REPORT_PARAMS.deliverables.sortFlightCollection},
                    ${REPORT_PARAMS.deliverables.uploadToProcessingClusters},
                }
            }`;
        case ReportInformationType.production:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.at},
                    ${REPORT_PARAMS.deliverables.controlManual},
                    ${REPORT_PARAMS.deliverables.atControl},
                    ${REPORT_PARAMS.deliverables.blockXml},
                    ${REPORT_PARAMS.deliverables.threeSm},
                    ${REPORT_PARAMS.deliverables.lasOpc},
                    ${REPORT_PARAMS.deliverables.atCloneSubsample},
                    ${REPORT_PARAMS.deliverables.ai3dTiles},
                    ${REPORT_PARAMS.deliverables.aiObj},
                    ${REPORT_PARAMS.deliverables.ply1Cm},
                }
            }`;
        /*case ReportInformationType.lidar:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.towerBaseE57Images},
                    ${REPORT_PARAMS.deliverables.liDARCollection},
                    ${REPORT_PARAMS.deliverables.liDARE57Model},
                    ${REPORT_PARAMS.deliverables.bLK360LeicaProcessingFTP},
                    ${REPORT_PARAMS.deliverables.bLK360FTP},
                    ${REPORT_PARAMS.deliverables.bLK360OGT},
                    ${REPORT_PARAMS.deliverables.e57Upload},
                }
            }`;*/
        /*case ReportInformationType.visuals:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.accessRoadVideo},
                    ${REPORT_PARAMS.deliverables.gatesSignageImages},
                    ${REPORT_PARAMS.deliverables.towerProfileImages},
                    ${REPORT_PARAMS.deliverables.electricalsFeedlinesImages},
                    ${REPORT_PARAMS.deliverables.rackElectronicsImages},
                    ${REPORT_PARAMS.deliverables.interiorBreakerBoxImages},
                    ${REPORT_PARAMS.deliverables.powerMetersBreakerBoxesImages},
                    ${REPORT_PARAMS.deliverables.inventorySortAndUploadGroundImages},
                    ${REPORT_PARAMS.deliverables.uploadTowerProfileAndAccessVideo},
                }
            }`;*/
        /*case ReportInformationType.externals:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.deliverables.orbitGtExport},
                    ${REPORT_PARAMS.deliverables.orbitGtImport},
                    ${REPORT_PARAMS.deliverables.orbitGtOptimize},
                    ${REPORT_PARAMS.deliverables.orbitGtUpload},
                    ${REPORT_PARAMS.deliverables.orbitGtPublish},
                    ${REPORT_PARAMS.deliverables.orbitGtLinkOtiq},
                    ${REPORT_PARAMS.deliverables.siteSeeXml},
                    ${REPORT_PARAMS.deliverables.siteSeeObj},
                    ${REPORT_PARAMS.deliverables.siteSee3dTile},
                    ${REPORT_PARAMS.deliverables.siteSeeTransfer},
                }
            }`;*/
        case ReportInformationType.deliverables:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.assetInfo.towerType},
                    ${REPORT_PARAMS.po.flightPOStatus},
                    ${REPORT_PARAMS.po.groundPOStatus},
                    ${REPORT_PARAMS.po.cancelNotes},
                    ${REPORT_PARAMS.assetInfo.region1or2},
                    ${REPORT_PARAMS.assetInfo.multiTower},
                    ${REPORT_PARAMS.po.pONumber},
                    ${REPORT_PARAMS.po.canceledBy},
                    ${REPORT_PARAMS.deliverables.flightCollection},
                    ${REPORT_PARAMS.staffs.flightCollectionPilot},
                    ${REPORT_PARAMS.staffs.flightCollectionVO},
                    ${REPORT_PARAMS.staffs.refly},
                    ${REPORT_PARAMS.deliverables.liDARCollection},
                    ${REPORT_PARAMS.staffs.groundCollectionTeam},
                    ${REPORT_PARAMS.assetInfo.structuralPriority},
                    ${REPORT_PARAMS.deliverables.geotag},
                    ${REPORT_PARAMS.deliverables.sortFlightCollection},
                    ${REPORT_PARAMS.deliverables.towerProfileImages},
                    ${REPORT_PARAMS.deliverables.accessRoadVideo},
                    ${REPORT_PARAMS.deliverables.siteSeeXml},
                    ${REPORT_PARAMS.deliverables.threeSm},
                    ${REPORT_PARAMS.deliverables.task1SOWFlightCollect},
                    ${REPORT_PARAMS.deliverables.orbitGtOptimize},
                    ${REPORT_PARAMS.deliverables.task2AIMLDelivered},
                    ${REPORT_PARAMS.deliverables.gatesSignageImages},
                    ${REPORT_PARAMS.deliverables.electricalsFeedlinesImages},
                    ${REPORT_PARAMS.deliverables.powerMetersBreakerBoxesImages},
                    ${REPORT_PARAMS.deliverables.bLK360OGT},
                    ${REPORT_PARAMS.deliverables.ai3dTiles},
                    ${REPORT_PARAMS.deliverables.rackElectronicsImages},
                    ${REPORT_PARAMS.deliverables.interiorBreakerBoxImages},
                    ${REPORT_PARAMS.deliverables.task3SOWGroundCollect},
                    ${REPORT_PARAMS.assetInfo.deliveredDate},
                    ${REPORT_PARAMS.assetInfo.oTIQLinks},
                }
            }`;
        case ReportInformationType.kml:
            return `
            query ReportQuery($vendorId: Int!) {
                output (format: "kml") {
                    format,
                },
                assets (
                    ${vendorIdParam},
                    ${orderIdParam},
                    ${mapBoundaryParam},
                    ${filterParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.assetInfo.towerType},
                    ${REPORT_PARAMS.assetInfo.latitude},
                    ${REPORT_PARAMS.assetInfo.longitude},
                }
            }`;
        case ReportInformationType.customerReport:
            return `
            query ReportQuery($vendorId: Int!) {
                assets (
                    ${vendorIdParam},
                    ${orderIdParam}
                )
                {
                    ${REPORT_PARAMS.assetInfo.siteCode},
                    ${REPORT_PARAMS.assetInfo.legacyIdCode},
                    ${REPORT_PARAMS.jobRequest.status},
                    ${REPORT_PARAMS.assetInfo.site},
                    ${REPORT_PARAMS.assetInfo.state},
                    ${REPORT_PARAMS.assetInfo.latitude},
                    ${REPORT_PARAMS.assetInfo.longitude},
                    ${REPORT_PARAMS.assetInfo.siteType},
                    ${REPORT_PARAMS.assetInfo.towerType},
                    ${REPORT_PARAMS.assetInfo.towerHeight},
                    ${REPORT_PARAMS.jobRequest.teamNumber},
                    ${REPORT_PARAMS.jobRequest.teamName},
                    ${REPORT_PARAMS.jobRequest.vehicleInfo},
                    ${REPORT_PARAMS.jobRequest.teamEmail},
                    ${REPORT_PARAMS.visitInfo.visitSite},
                    ${REPORT_PARAMS.visitInfo.visitRegion},
                    ${REPORT_PARAMS.visitInfo.visitLatitude},
                    ${REPORT_PARAMS.visitInfo.visitLongitude},
                    ${REPORT_PARAMS.visitInfo.visitSiteType},
                    ${REPORT_PARAMS.visitInfo.visitTowerType},
                    ${REPORT_PARAMS.visitInfo.visitTowerHeight},
                    ${REPORT_PARAMS.workflow.poReceived},
                    ${REPORT_PARAMS.workflow.flightPermitSubmitted},
                    ${REPORT_PARAMS.workflow.flightPermitApproved},
                    ${REPORT_PARAMS.workflow.plannedDroneFlight},
                    ${REPORT_PARAMS.workflow.actualDroneFlight},
                    ${REPORT_PARAMS.workflow.dataUploadedToServers},
                    ${REPORT_PARAMS.workflow.threeDModelProcessed},
                    ${REPORT_PARAMS.workflow.otiqAiProcessed},
                    ${REPORT_PARAMS.workflow.measurementExtraction},
                    ${REPORT_PARAMS.workflow.drawingCompleted},
                    ${REPORT_PARAMS.workflow.StructureAnalysis},
                    ${REPORT_PARAMS.workflow.reportSubmitted},
                    ${REPORT_PARAMS.workflow.siteAccepted},
                    ${REPORT_PARAMS.workflow.siteInvoiceSubmitted},
                }
            }`;
        }
    return '';
  }
}
